import React, { useState, useCallback, useEffect } from "react";
import AppointmentList from "./AppointmentList";
import ViewAppointment from "./ViewAppointment";
import CreateAppointment from "./CreateAppointment";
import {
    APPOINTMENT_CREATE_PRESET_STORAGE_KEY,
    fetchAppointmentById,
} from "../../services/appointment/appointment";

function readAppointmentCreatePreset() {
    try {
        const raw = sessionStorage.getItem(APPOINTMENT_CREATE_PRESET_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        sessionStorage.removeItem(APPOINTMENT_CREATE_PRESET_STORAGE_KEY);
        if (
            parsed?.AppointmentType &&
            Array.isArray(parsed.sourceKeys) &&
            parsed.sourceKeys.length
        ) {
            return parsed;
        }
    } catch {
        /* ignore */
    }
    return null;
}

export default function AppointmentManagement() {
    const [activeRecord, setActiveRecord] = useState(null);
    const [listRefreshKey, setListRefreshKey] = useState(0);

    const applyPresetFromStorage = useCallback(() => {
        const preset = readAppointmentCreatePreset();
        if (preset) {
            setActiveRecord({ mode: "create", preset });
        }
    }, []);

    useEffect(() => {
        applyPresetFromStorage();
        const onPreset = () => applyPresetFromStorage();
        window.addEventListener("partner-portal:appointment-preset-updated", onPreset);
        return () =>
            window.removeEventListener(
                "partner-portal:appointment-preset-updated",
                onPreset
            );
    }, [applyPresetFromStorage]);

    const onlyFormVisible = !!activeRecord;
    const headerTitle = !onlyFormVisible
        ? "Appointments"
        : activeRecord?.mode === "create"
          ? "Create Appointment"
          : "View Appointment";

    const closeForm = () => {
        setActiveRecord(null);
    };

    const bumpListRefresh = useCallback(() => {
        setListRefreshKey((k) => k + 1);
    }, []);

    return (
        <div className="card shadow-lg main-card">
            <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                <h4 className="mb-0 h6 fw-semibold">{headerTitle}</h4>
            </div>

            {onlyFormVisible && (
                <nav
                    aria-label="breadcrumb"
                    className="px-3 py-2 border-bottom bg-light"
                >
                    <ol
                        className="breadcrumb mb-0 align-items-center"
                        style={{ "--bs-breadcrumb-divider": "'/'" }}
                    >
                        <li className="breadcrumb-item">
                            <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none text-muted"
                                onClick={closeForm}
                            >
                                Appointments
                            </button>
                        </li>
                        <li
                            className="breadcrumb-item active fw-semibold text-primary"
                            aria-current="page"
                        >
                            {activeRecord?.mode === "create"
                                ? "Create Appointment"
                                : "View Appointment"}
                        </li>
                    </ol>
                </nav>
            )}

            <div className="card-body">
                {!onlyFormVisible ? (
                    <AppointmentList
                        key={listRefreshKey}
                        onCreate={() =>
                            setActiveRecord({ mode: "create", data: null })
                        }
                        onView={async (id) => {
                            const apiData = await fetchAppointmentById(id);
                            setActiveRecord({
                                mode: "view",
                                data: apiData,
                            });
                        }}
                    />
                ) : activeRecord?.mode === "create" ? (
                    <CreateAppointment
                        key={
                            activeRecord?.preset
                                ? JSON.stringify(activeRecord.preset)
                                : "create-default"
                        }
                        initialPreset={activeRecord?.preset}
                        onCancel={closeForm}
                        onCreated={() => {
                            closeForm();
                            bumpListRefresh();
                        }}
                    />
                ) : (
                    <ViewAppointment
                        initialValues={activeRecord?.data || {}}
                        onCancel={closeForm}
                    />
                )}
            </div>
        </div>
    );
}
